/**
 * Name Contamination Blocker - CRITICAL SECURITY COMPONENT
 * 
 * This utility provides comprehensive protection against name contamination,
 * specifically preventing "Vinh" or other unwanted names from appearing in
 * personalized content when they shouldn't.
 */

export interface NameValidationResult {
  isClean: boolean;
  contaminatedNames: string[];
  cleanedValue?: string;
}

// List of names that should NEVER appear in personalized content
const CONTAMINATED_NAMES = new Set([
  'vinh', 'Vinh', 'VINH',
  'olivia chen', 'Olivia Chen', 'OLIVIA CHEN', // Invented full names
  'friend', // Generic fallback should not be personalized
  'there', // Generic fallback should not be personalized
]);

// List of names that are safe for personalization
const SAFE_GENERIC_NAMES = new Set([
  'friend', 'there', 'listener'
]);

/**
 * Validates if a name is safe for personalization
 */
export const validatePersonalizationName = (name: string): NameValidationResult => {
  if (!name || name.trim() === '') {
    return { isClean: true, contaminatedNames: [] };
  }

  const trimmedName = name.trim();
  const lowerName = trimmedName.toLowerCase();

  // Check for contaminated names
  const contaminatedNames: string[] = [];
  
  if (CONTAMINATED_NAMES.has(trimmedName) || CONTAMINATED_NAMES.has(lowerName)) {
    contaminatedNames.push(trimmedName);
  }

  // Additional pattern checks for variations
  if (lowerName.includes('vinh')) {
    contaminatedNames.push(trimmedName);
  }
  
  // Check for invented full names like "Olivia Chen"
  if (lowerName.includes('olivia chen') || lowerName.includes('chen')) {
    contaminatedNames.push(trimmedName);
  }

  return {
    isClean: contaminatedNames.length === 0,
    contaminatedNames,
    cleanedValue: contaminatedNames.length > 0 ? 'there' : trimmedName
  };
};

/**
 * Sanitizes profile data to remove any contaminated names
 */
export const sanitizeProfileData = (profileData: any): any => {
  if (!profileData) return profileData;

  const sanitized = { ...profileData };

  // Check and sanitize first_name
  if (sanitized.first_name) {
    const validation = validatePersonalizationName(sanitized.first_name);
    if (!validation.isClean) {
      console.error('🚨 CONTAMINATED PROFILE first_name DETECTED:', validation.contaminatedNames);
      delete sanitized.first_name; // Remove contaminated name entirely
    }
  }

  // Check and sanitize preferred_name
  if (sanitized.preferred_name) {
    const validation = validatePersonalizationName(sanitized.preferred_name);
    if (!validation.isClean) {
      console.error('🚨 CONTAMINATED PROFILE preferred_name DETECTED:', validation.contaminatedNames);
      delete sanitized.preferred_name; // Remove contaminated name entirely
    }
  }

  return sanitized;
};

/**
 * Validates and blocks contaminated allowedNames policy
 */
export const validateAllowedNamesPolicy = (allowedNames: any): any => {
  if (!allowedNames) return allowedNames;

  const sanitized = { ...allowedNames };

  // Check firstName
  if (sanitized.firstName) {
    const validation = validatePersonalizationName(sanitized.firstName);
    if (!validation.isClean) {
      console.error('🚨 CONTAMINATED allowedNames firstName DETECTED:', validation.contaminatedNames);
      return null; // Block entire policy if contaminated
    }
  }

  // Check preferredName
  if (sanitized.preferredName) {
    const validation = validatePersonalizationName(sanitized.preferredName);
    if (!validation.isClean) {
      console.error('🚨 CONTAMINATED allowedNames preferredName DETECTED:', validation.contaminatedNames);
      return null; // Block entire policy if contaminated
    }
  }

  // Check groupNames
  if (sanitized.groupNames && Array.isArray(sanitized.groupNames)) {
    const cleanGroupNames = sanitized.groupNames.filter(name => {
      const validation = validatePersonalizationName(name);
      if (!validation.isClean) {
        console.error('🚨 CONTAMINATED allowedNames groupName DETECTED:', validation.contaminatedNames);
        return false;
      }
      return true;
    });
    
    if (cleanGroupNames.length !== sanitized.groupNames.length) {
      sanitized.groupNames = cleanGroupNames;
    }
  }

  return sanitized;
};

/**
 * Final safety check for generated content
 */
export const blockContaminatedContent = (content: string, userProfileName?: string): string => {
  if (!content) return content;

  let cleanedContent = content;
  let replacements = 0;

  // Replace any contaminated names with the correct profile name or generic fallback
  const replacementName = userProfileName || 'there';
  
  cleanedContent = cleanedContent.replace(/\bVinh\b/g, () => {
    replacements++;
    console.error('🚨 BLOCKED CONTAMINATED NAME "Vinh" IN FINAL CONTENT');
    return replacementName;
  });
  
  cleanedContent = cleanedContent.replace(/\bOlivia\s+Chen\b/g, () => {
    replacements++;
    console.error('🚨 BLOCKED CONTAMINATED FULL NAME "Olivia Chen" IN FINAL CONTENT');
    return replacementName;
  });

  if (replacements > 0) {
    console.error(`🚨 CONTAMINATION CLEANUP: Replaced ${replacements} instances of contaminated names`);
  }

  return cleanedContent;
};

/**
 * Debug helper to trace name resolution flow
 */
export const debugNameResolution = (
  context: string,
  profileData?: any,
  allowedNames?: any,
  resolvedName?: string
): void => {
  console.log(`🔍 NAME RESOLUTION DEBUG [${context}]:`, {
    profileFirstName: profileData?.first_name,
    profilePreferredName: profileData?.preferred_name,
    allowedNamesFirstName: allowedNames?.firstName,
    allowedNamesPreferredName: allowedNames?.preferredName,
    finalResolvedName: resolvedName,
    isContaminated: resolvedName === 'Vinh' || resolvedName === 'vinh',
    shouldUseProfile: !!profileData?.first_name,
    profileDataPriority: 'HIGHEST'
  });

  if (resolvedName === 'Vinh' || resolvedName === 'vinh') {
    console.error('🚨 CRITICAL ERROR: "Vinh" detected in name resolution!');
    console.error('🚨 This should NEVER happen - profile data should override everything');
  }
};